import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./OrganizationsPage.css";
import { useOrganizations } from "../../../hooks/useOrganizations";
import {
  createOrganizationAsync,
  deleteOrganizationAsync,
  updateOrganizationAsync,
} from "../../../api/OrganizationApi";
import { Constants } from "../../../constants/constants";
import { CreateOrganizationRequest, OrganizationResponse } from "../../../models/OrganizationModels";
import OrganizationModal, {
  OrganizationFormValues,
  OrganizationModalMode,
  toUpdateOrganizationRequest,
} from "./OrganizationCreateModal";
import { useAuth } from "../../../hooks/useAuth";
import Toast from "../../../components/Toast";

const OrganizationsPage = () => {
  const {
    organizations,
    pagination,
    loading,
    error,
    setPage,
    reload,
    addLocal,
    updateLocal,
    removeLocal,
  } = useOrganizations();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<OrganizationModalMode>("create");
  const [selectedOrg, setSelectedOrg] = useState<OrganizationResponse | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const startPage = (pagination.page - 1) * pagination.pageSize + 1;
  const endPage = Math.min(
    pagination.page * pagination.pageSize,
    pagination.totalCount,
  );

  const openModal = (
    mode: OrganizationModalMode,
    org: OrganizationResponse | null,
  ) => {
    setModalMode(mode);
    setSelectedOrg(org);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleSubmit = async (form: OrganizationFormValues) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

    if (modalMode === "create") {
      const payload: CreateOrganizationRequest = {
        name: form.name,
        slug: form.slug,
        organizationType: form.organizationType,
        phone: form.phone || undefined,
        email: form.email || undefined,
        description: form.description || undefined,
        address: form.address || undefined,
        city: form.city || undefined,
        area: form.area || undefined,
        latitude: form.latitude ? Number(form.latitude) : undefined,
        longitude: form.longitude ? Number(form.longitude) : undefined,
        googleMapsUrl: form.googleMapsUrl || undefined,
        facebookUrl: form.facebookUrl || undefined,
        instagramUrl: form.instagramUrl || undefined,
        youtubeUrl: form.youtubeUrl || undefined,
        linkedInUrl: form.linkedInUrl || undefined,
        tiktokUrl: form.tiktokUrl || undefined,
        websiteTitle: form.websiteTitle || undefined,
        websiteTagline: form.websiteTagline || undefined,
        logoFile: form.logoFile ?? undefined,
        bannerFile: form.bannerFile ?? undefined,
        isActive: true,
      };

      setSubmitting(true);
      try {
        const res = await createOrganizationAsync(token, payload);
        if (res.success && res.result) {
          addLocal(res.result);
          closeModal();
          setToast({
            message: "Organization created successfully!",
            type: "success",
          });
        } else {
          setToast({
            message: res.message || "Failed to create organization",
            type: "error",
          });
        }
      } catch (err) {
        console.error(err);
        setToast({
          message: "Failed to create organization. Please try again.",
          type: "error",
        });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (modalMode === "edit" && selectedOrg) {
      setSubmitting(true);
      try {
        const res = await updateOrganizationAsync(
          token,
          selectedOrg.id,
          toUpdateOrganizationRequest(form, selectedOrg),
        );
        if (res.success && res.result) {
          updateLocal(res.result);
          closeModal();
          setToast({
            message: "Organization updated successfully!",
            type: "success",
          });
        } else {
          setToast({
            message: res.message || "Failed to update organization",
            type: "error",
          });
        }
      } catch (err) {
        console.error(err);
        setToast({
          message: "Failed to update organization. Please try again.",
          type: "error",
        });
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleToggleActive = async (org: OrganizationResponse) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    const nextActive = !org.isActive;

    updateLocal({ ...org, isActive: nextActive });

    try {
      const res = await updateOrganizationAsync(token, org.id, {
        isActive: nextActive,
      });
      if (res.success && res.result) {
        updateLocal({ ...res.result, isActive: res.result.isActive ?? nextActive });
        setToast({
          message: res.result.isActive ?? nextActive
            ? "Organization activated"
            : "Organization deactivated",
          type: "success",
        });
      } else {
        updateLocal({ ...org, isActive: org.isActive });
        setToast({
          message: res.message || "Failed to update organization",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      updateLocal({ ...org, isActive: org.isActive });
      setToast({
        message: "Failed to update organization status. Please try again.",
        type: "error",
      });
    }
  };

  const handleDelete = async (org: OrganizationResponse) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      const res = await deleteOrganizationAsync(token, org.id);
      if (res.success) {
        removeLocal(org.id);
        setToast({
          message: "Organization deleted successfully!",
          type: "success",
        });
      } else {
        setToast({
          message: res.message || "Failed to delete organization",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Failed to delete organization. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return <div className="orgs-empty">Loading organizations...</div>;
    }

    if (error) {
      return (
        <div className="orgs-empty">
          <p>{error}</p>
          <button className="orgs-btn orgs-btn--secondary" onClick={reload}>
            Retry
          </button>
        </div>
      );
    }

    if (organizations.length === 0) {
      return (
        <div className="orgs-empty">
          No organizations found. Click "Add Organization" to create one.
        </div>
      );
    }

    return (
      <div className="orgs-table-wrap">
        <table className="orgs-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>City</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {organizations.map((org) => (
              <tr key={org.id} onDoubleClick={() => openModal("view", org)}>
                <td>
                  <div className="orgs-name">
                    <span className="orgs-avatar">
                      {org.name.charAt(0).toUpperCase()}
                    </span>
                    {org.name}
                  </div>
                </td>
                <td>{org.email}</td>
                <td>{org.city}</td>
                <td>
                  <button
                    className={`orgs-switch ${org.isActive ? "on" : ""}`}
                    role="switch"
                    aria-checked={org.isActive}
                    aria-label={`Toggle ${org.name} status`}
                    onClick={() => handleToggleActive(org)}
                  >
                    <span className="orgs-switch__thumb" />
                  </button>
                </td>
                <td className="orgs-actions">
                  {user?.isPlatformOwner && (
                    <button
                      className="orgs-btn orgs-btn--small"
                      onClick={() =>
                        navigate(`/organizations/${org.id}/subscription`)
                      }
                      title="Activate or extend this organization's subscription"
                    >
                      Subscription
                    </button>
                  )}
                  <button
                    className="orgs-btn orgs-btn--small"
                    onClick={() => openModal("view", org)}
                  >
                    View
                  </button>
                  <button
                    className="orgs-btn orgs-btn--small"
                    onClick={() => openModal("edit", org)}
                  >
                    Edit
                  </button>
                  <button
                    className="orgs-btn orgs-btn--small orgs-btn--danger"
                    onClick={() => {
                      if (window.confirm(`Delete "${org.name}"?`)) {
                        handleDelete(org);
                      }
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="orgs-page">
      <div className="orgs-header">
        <div>
          <h2>Organizations</h2>
          <p>Manage all organizations (clinics, parlors, etc.)</p>
        </div>
        <button
          className="orgs-btn orgs-btn--primary"
          onClick={() => openModal("create", null)}
        >
          + Add Organization
        </button>
      </div>

      {renderBody()}

      {!loading && !error && organizations.length > 0 && (
        <div className="orgs-pagination">
          <span className="orgs-pagination__info">
            Showing {startPage}–{endPage} of {pagination.totalCount}
          </span>
          <div className="orgs-pagination__controls">
            <button
              className="orgs-btn orgs-btn--secondary"
              disabled={!pagination.hasPrevious}
              onClick={() => setPage(pagination.page - 1)}
            >
              Previous
            </button>
            <span className="orgs-pagination__page">
              Page {pagination.page} of {Math.max(1, pagination.totalPages)}
            </span>
            <button
              className="orgs-btn orgs-btn--secondary"
              disabled={!pagination.hasNext}
              onClick={() => setPage(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <OrganizationModal
        isOpen={modalOpen}
        mode={modalMode}
        organization={selectedOrg}
        onClose={closeModal}
        onSubmit={handleSubmit}
        submitting={submitting}
      />
    </div>
  );
};

export default OrganizationsPage;